import axios from "axios";
import { localStore } from "@/lib/storage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// ─────────────────────────────────────────────────────────────
// Axios instance
// ─────────────────────────────────────────────────────────────
export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
  timeout: 20000, // 20 s : au-delà on considère le back injoignable
});

let currentOrgId = null;
export function setActiveOrgId(orgId) {
  currentOrgId = orgId;
  if (orgId) localStore.set("fonbex_org", orgId);
  else localStore.remove("fonbex_org");
}
export function getActiveOrgId() {
  if (currentOrgId) return currentOrgId;
  const stored = localStore.get("fonbex_org");
  currentOrgId = stored;
  return stored;
}

api.interceptors.request.use((config) => {
  const orgId = getActiveOrgId();
  if (orgId) config.headers["X-Organization-Id"] = orgId;
  // Compteur pour la relance progressive
  if (config.__retryCount == null) config.__retryCount = 0;
  return config;
});

// ─────────────────────────────────────────────────────────────
// Relance automatique (transient errors) + refresh 401 unique
//
// Politique :
//   • Codes qui NE relancent PAS (fonctionnels) : 400, 401 (traité à part),
//     403, 404, 409, 422.
//   • Codes qui relancent : 408 (timeout), 425, 429, 500, 502, 503, 504,
//     ECONNABORTED, network errors sans réponse.
//   • Max 2 relances (donc 3 tentatives au total).
//   • Délai : 500 ms puis 1 500 ms.
// ─────────────────────────────────────────────────────────────
const MAX_RETRIES = 2;
const RETRY_DELAYS_MS = [500, 1500];
const RETRYABLE_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);
const REFRESH_EXCLUDE = /\/auth\/(login|refresh|logout|register)/;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** Renvoie true si la requête est éligible à une relance automatique. */
function shouldRetry(err, config) {
  if (!config || config.__noRetry) return false;
  if ((config.__retryCount || 0) >= MAX_RETRIES) return false;
  // GET / HEAD idempotents uniquement (protège les POST/PUT/DELETE
  // sensibles d'un double effet).
  const method = (config.method || "get").toLowerCase();
  if (method !== "get" && method !== "head") return false;
  // Erreur réseau ou timeout : pas de réponse HTTP
  if (!err.response) return true;
  return RETRYABLE_STATUSES.has(err.response.status);
}

let refreshInFlight = null;
async function tryRefreshSession() {
  if (!refreshInFlight) {
    refreshInFlight = axios
      .post(`${API_BASE}/auth/refresh`, null, { withCredentials: true })
      .finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const config = err.config || {};

    // ─── 401 : tentative de refresh + rejeu unique de la requête ───
    if (
      err.response?.status === 401 &&
      !config.__triedRefresh &&
      !REFRESH_EXCLUDE.test(config.url || "")
    ) {
      config.__triedRefresh = true;
      try {
        await tryRefreshSession();
        return api.request(config);
      } catch (_e) {
        // Refresh raté — on laisse remonter le 401 pour redirect login
        err.__sessionExpired = true;
      }
    }

    // ─── Relance progressive pour erreurs transitoires ───
    if (shouldRetry(err, config)) {
      const attempt = config.__retryCount || 0;
      config.__retryCount = attempt + 1;
      const delay = RETRY_DELAYS_MS[attempt] || 1500;
      await sleep(delay);
      return api.request(config);
    }

    // ─── Normalisation du champ detail (Pydantic renvoie parfois un array) ───
    if (err?.response?.data) {
      const raw = err.response.data.detail;
      if (raw != null && typeof raw !== "string") {
        err.response.data.raw_detail = raw;
        err.response.data.detail = formatApiError(raw);
      }
    }
    return Promise.reject(err);
  },
);

// ─────────────────────────────────────────────────────────────
// Formatters (messages user-friendly, catégorisés)
// ─────────────────────────────────────────────────────────────

export function formatApiError(detail) {
  if (detail == null) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/**
 * Prend une erreur axios brute et renvoie un message utilisateur exploitable,
 * catégorisé selon le contexte (réseau / session / accès / serveur / autre).
 *
 * Utiliser `.kind` pour brancher un traitement UI spécifique :
 *   `network` | `session_expired` | `forbidden` | `not_found`
 *   | `rate_limited` | `server` | `unknown`.
 */
export function categorizeError(err) {
  if (!err) return { kind: "unknown", message: "Une erreur est survenue." };
  if (err.__sessionExpired || err.response?.status === 401) {
    return {
      kind: "session_expired",
      message: "Votre session a expiré. Veuillez vous reconnecter.",
    };
  }
  if (err.response) {
    const status = err.response.status;
    const detail = err.response.data?.detail;
    if (status === 403)
      return { kind: "forbidden", message: detail || "Vous n'avez pas accès à cette ressource." };
    if (status === 404)
      return { kind: "not_found", message: detail || "Ressource introuvable." };
    if (status === 429)
      return { kind: "rate_limited", message: "Trop de tentatives. Merci de patienter." };
    if (status >= 500)
      return {
        kind: "server",
        message: "Le service rencontre un problème temporaire. Merci de réessayer dans un instant.",
      };
    return { kind: "unknown", message: detail || `Erreur (HTTP ${status}).` };
  }
  if (err.code === "ECONNABORTED")
    return { kind: "network", message: "Le serveur met trop de temps à répondre. Vérifiez votre connexion." };
  if (err.message && /Network Error/i.test(err.message))
    return { kind: "network", message: "Connexion momentanément indisponible. Vérifiez votre connexion internet." };
  return { kind: "unknown", message: err.message || "Une erreur est survenue." };
}

/** Ancien helper conservé pour compat — délégué à ``categorizeError``. */
export function formatAxiosError(err) {
  return categorizeError(err).message;
}
